'use client';
import { FC, JSX, useCallback, useEffect, useState } from 'react';

import Modal, { ModalPropTypes } from '@/layouts/Modal';
import { debounce } from '@/utils/helper';
import { CloseIcon, LocationPinIcon, MagnifyingGlassIcon } from '@/icons';
import { Svg } from '@/components';
import { useLocation } from '@/context/locationContext';
import { autocomplete } from './google';
import { PlaceAutocompleteResult } from '@googlemaps/google-maps-services-js';
import { getGooglePlaceDetails } from '@/app/actions';
import { storage } from '@/utils/storage';
import { logger } from '@/config';
import { DEFAULT_STOCK_ID, DEFAULT_STORE_CODE } from '@/constants';
import { useCleverTap } from '@/context/clevertapContext';
import { useCart } from '@/context/cartContext';

type PropTypes = ModalPropTypes & {
  redirectTo?: string;
  asGuest?: boolean;
  message?: string;
};

interface GooglePlaceData {
  store_code: string;
  default_store_code: string;
  stock_id: string;
  wizzy_api_key: string;
  wizzy_store_id: string;
  result: Array<{
    lat: number;
    lng: number;
    city?: string;
    state?: string;
  }>;
  [key: string]: any; // Add index signature for dynamic access
}

const SearchManually: FC<PropTypes> = ({ show, onClose }): JSX.Element => {
  const { pushEvent } = useCleverTap();
  const { cartItems } = useCart();
  const [predictions, setPredictions] = useState<PlaceAutocompleteResult[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const {
    handleSuggestionClick,
    setAddress,
    setPendingAddressChange,
    pendingAddressChange,
    emptyCart,
    setEmptyCart,
    setShowStoreChangeModal,
    setShowNonServiceablePopup,
    isDelivery,
    setShowDeliveryModal,
    setShowHomeDelivery,
    setHeaderLocationTitle,
    setDeliveryType,
    setAddressString,
    isProcessingChange,
    setIsProcessingChange,
    handleStoreChange,
    setAddressSelected,
  } = useLocation();

  const fetchPredictions = async (inputValue: string) => {
    try {
      if (!inputValue.trim()) {
        setPredictions([]);
        return;
      }

      const predictions = await autocomplete(inputValue);
      if (!predictions) {
        throw new Error('Failed to fetch location predictions');
      }

      pushEvent({
        event: 'Location Searched',
        data: {
          'Search Query': inputValue,
          'Number of Results': predictions?.length,
          'Page URL': window.location.href,
          'Page Title': document.title,
        },
      });
      setPredictions(predictions);
      setError(null);
    } catch (error: any) {
      logger.error('Error fetching predictions:', error);
      setError('Failed to fetch locations. Please try again.');
      setPredictions([]);
    }
  };

  const debouncedLocationSearch = useCallback(
    debounce(fetchPredictions, 500),
    []
  );

  const onSearchModalClose = useCallback(() => {
    setInput('');
    setPredictions([]);
    setError(null);
    onClose();
  }, [onClose]);

  const processAddressChange = async (
    prediction: any,
    data: GooglePlaceData
  ) => {
    try {
      const selectedAddress = prediction.description;
      setInput(selectedAddress);
      setAddress(selectedAddress);

      if (data?.store_code !== '') {
        // Validate required store data
        const requiredFields = [
          'wizzy_api_key',
          'wizzy_store_id',
          'store_code',
          'stock_id',
        ];
        for (const field of requiredFields) {
          if (!data[field]) {
            throw new Error(`Missing required store data: ${field}`);
          }
        }

        storage.set('address', prediction.structured_formatting?.main_text);
        setHeaderLocationTitle(prediction.structured_formatting?.main_text);
        storage.set(
          'addressTitle',
          prediction.structured_formatting?.main_text
        );
        storage.set(
          'addressDescription',
          prediction.structured_formatting?.secondary_text
        );
        storage.set('wizzy_api_key', data.wizzy_api_key);
        storage.set('wizzy_store_id', data.wizzy_store_id);
        storage.set(
          'store_code',
          data.store_code || data.default_store_code || DEFAULT_STORE_CODE
        );
        storage.set('stock_id', data.stock_id || DEFAULT_STOCK_ID);
      }

      if (
        data?.result?.[0]?.lat &&
        data?.result?.[0]?.lng &&
        data?.store_code
      ) {
        storage.set('lat', String(data.result[0].lat));
        storage.set('lng', String(data.result[0].lng));
      }

      let cityPart = '';
      let statePart = '';

      if (
        data?.result?.[0]?.city &&
        data?.result?.[0]?.state &&
        data?.store_code
      ) {
        cityPart = data.result[0].city;
        statePart = data.result[0].state;
      } else {
        const addressParts = selectedAddress
          .split(',')
          .map((part: string) => part.trim());
        cityPart = addressParts[addressParts.length - 3];
        statePart = addressParts[addressParts.length - 2];
      }

      if (cityPart && statePart && data?.store_code) {
        storage.set('city', cityPart);
        storage.set('state', statePart);
      }
    } catch (error: any) {
      logger.error('Error in processAddressChange:', error);
      throw new Error(error.message || 'Failed to process address change');
    }
  };

  const handleSuggestionSelect = async (prediction: any, input: string) => {
    try {
      setIsLoading(true);
      setError(null);
      setAddressSelected(true);
      onSearchModalClose();
      setIsProcessingChange(true);
      storage.set('selectedAddressId', '');

      const googlePlaceDetails = await getGooglePlaceDetails(
        prediction.place_id
      );
      if (!googlePlaceDetails) {
        throw new Error('Failed to fetch place details');
      }

      pushEvent({
        event: 'Suggested Location Selected',
        data: {
          'Search Query': input,
          'Selected Location': prediction.description,
          'Selected Location Title':
            prediction.structured_formatting?.main_text,
          'Selected Location Sub-Title':
            prediction.structured_formatting?.secondary_text,
          'Page URL': window.location.href,
          'Page Title': document.title,
        },
      });

      const { data } = JSON.parse(googlePlaceDetails) as {
        data: GooglePlaceData;
      };
      if (!data) {
        throw new Error('Invalid place details response');
      }

      if (data.store_code) {
        storage.set('deliveryType', 'home');
        setDeliveryType('home');
      }

      const storeCode =
        data?.store_code || data?.default_store_code || DEFAULT_STORE_CODE;
      const currentStoreCode = storage.get('store_code');

      // Handle store change for cart section
      if (data.store_code) {
        if (
          currentStoreCode &&
          data.store_code !== currentStoreCode &&
          cartItems &&
          cartItems.length > 0
        ) {
          setPendingAddressChange({ prediction, data });
          setShowStoreChangeModal(true);
          onSearchModalClose();
          return;
        } else if (currentStoreCode && data.store_code !== currentStoreCode) {
          const storeChangeSuccess = await handleStoreChange({
            stock_id: data?.stock_id,
            store_code: storeCode,
            default_store_code: data?.default_store_code,
            wizzy_api_key: data?.wizzy_api_key,
            wizzy_store_id: data?.wizzy_store_id,
          });

          if (!storeChangeSuccess) {
            throw new Error('Failed to change store');
          }
        }
      } else {
        if (!isDelivery) {
          await handleSuggestionClick({
            prediction: prediction,
            address: prediction.description,
            skipToast: false,
            isDeliveryPopup: true,
          });
          setShowNonServiceablePopup(true);
          onSearchModalClose();
          return;
        } else {
          setShowDeliveryModal(true);
          setShowHomeDelivery(true);
        }
      }

      // Process address change and handle callbacks
      await handleSuggestionClick({
        prediction: prediction,
        address: prediction.description,
        skipToast: false,
        isDeliveryPopup: currentStoreCode !== storeCode ? false : true,
      });

      setAddressString(prediction.description);
      storage.set('addressString', prediction.description);

      await processAddressChange(prediction, data);
      onSearchModalClose();
    } catch (error: any) {
      logger.error('Error in handleSuggestionSelect:', error);
      setError(error.message || 'Failed to process location selection');
      setAddressString('');
      storage.set('addressString', '');
    } finally {
      setIsLoading(false);
      setIsProcessingChange(false);
    }
  };

  // Update the useEffect to properly handle the emptyCart state
  useEffect(() => {
    const handlePendingChange = async () => {
      if (emptyCart && pendingAddressChange && !isProcessingChange) {
        try {
          setIsProcessingChange(true);
          await processAddressChange(
            pendingAddressChange.prediction,
            pendingAddressChange.data
          );
          setShowStoreChangeModal(false);
          setPendingAddressChange(null);
          setEmptyCart(false);
        } catch (error: any) {
          logger.error('Error handling pending change:', error);
          setError(error.message || 'Failed to process pending change');
          setEmptyCart(false);
          setPendingAddressChange(null);
          setShowStoreChangeModal(false);
        } finally {
          setIsProcessingChange(false);
        }
      }
    };

    handlePendingChange();
  }, [emptyCart, pendingAddressChange, isProcessingChange]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInput(value);
    setError(null);

    if (!value.trim()) {
      setPredictions([]);
      return;
    }

    if (value.trim().length >= 3) {
      debouncedLocationSearch(value);
    } else {
      setPredictions([]);
    }
  };

  // Add useEffect for autofocus
  useEffect(() => {
    if (show) {
      const timer = setTimeout(() => {
        const searchInput = document.getElementById('locationSearchBar');
        if (searchInput) {
          searchInput.focus();
          if ('virtualKeyboard' in navigator) {
            (navigator as any).virtualKeyboard?.show();
          }
        }
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [show]);

  return (
    <Modal
      show={show}
      onClose={onSearchModalClose}
      closeOnOutsideClick={false}
      id='search-manually'
      heightClassName='max-md:h-full max-md:max-h-screen'
      style='max-md:h-full max-md:max-h-screen z-60'>
      <div className='relative flex h-full w-full flex-col max-lg:pb-0'>
        {/* Header */}
        <div aria-label='modal-header' className='relative bg-white p-4'>
          <div className='text-left text-xl font-bold md:text-2xl'>
            Enter your delivery location
          </div>
        </div>

        {/* Search Input */}
        <div className='relative px-4'>
          <input
            id='locationSearchBar'
            type='search'
            spellCheck='false'
            role='search'
            autoComplete='off'
            aria-label='search-bar'
            placeholder='Search a new address'
            value={input}
            onChange={handleInputChange}
            autoFocus
            disabled={isLoading}
            className='search-input w-full rounded-lg border border-[#38373A] bg-white px-4 py-3 pr-10 outline-hidden backdrop-blur-xs placeholder:select-none placeholder:truncate placeholder:text-sm placeholder:text-[#38373A] placeholder:opacity-70 disabled:opacity-50'
            onFocus={e => {
              e.target.focus();
              if ('virtualKeyboard' in navigator) {
                (navigator as any).virtualKeyboard?.show();
              }
            }}
          />
          {input.trim().length <= 0 ? (
            <Svg className='absolute right-7 top-4 h-5 w-5 stroke-[#A0383F]'>
              <MagnifyingGlassIcon />
            </Svg>
          ) : (
            <button onClick={() => setInput('')} disabled={isLoading}>
              <Svg className='absolute right-7 top-4 h-5 w-5 cursor-pointer'>
                <CloseIcon />
              </Svg>
            </button>
          )}
        </div>
        <div className='mt-2 w-full px-4 text-left text-[10px] font-semibold text-[#38373A]'>
          Our services are currently available in Gurugram, Delhi, Hyderabad and
          Bangalore
        </div>

        {/* Error Message */}
        {error && (
          <div className='mt-2 px-4 text-left text-sm text-red-500'>
            {error}
          </div>
        )}

        {/* Search Results Section */}
        <div className='mt-1 flex-1 overflow-y-auto px-4 pb-4'>
          {isLoading ? (
            <div className='flex h-20 items-center justify-center'>
              <div className='base-loading h-8 w-8' />
            </div>
          ) : input.trim().length >= 3 && predictions.length > 0 ? (
            <div className='h-full'>
              <ul className='divide-y divide-gray-100'>
                {predictions.map(prediction => (
                  <li
                    key={prediction.place_id}
                    title={prediction.description}
                    aria-label={prediction.description}
                    className='cursor-pointer py-3 text-left transition-colors hover:bg-gray-50'
                    onClick={() => handleSuggestionSelect(prediction, input)}>
                    <div className='flex items-center gap-3'>
                      <div className='shrink-0 pt-1'>
                        <Svg className='h-6 w-6 stroke-[#38383A] stroke-[1.5]'>
                          <LocationPinIcon />
                        </Svg>
                      </div>
                      <div className='flex flex-col'>
                        <span className='text-base font-semibold text-[#38383A]'>
                          {prediction.structured_formatting?.main_text}
                        </span>
                        <span className='text-xs text-[#38373A]'>
                          {prediction.structured_formatting?.secondary_text}
                        </span>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          ) : input.trim().length > 4 && predictions.length === 0 ? (
            <div className='flex h-20 items-center justify-center text-sm text-gray-500'>
              No locations found
            </div>
          ) : null}
        </div>
      </div>
    </Modal>
  );
};

export default SearchManually;
